import moment from 'moment';
// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import {
    isToday,
    isYesterday,
    getTinyTimestamp,
    isWithinPastWeek,
    isSameYear,
    isWithinPastTimespan,
} from 'nutshell-core/date-time';
import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import {ThreadBotStatus, ThreadType, CampaignType} from 'nutshell-graphql-types';
import type {
    InboxMessageFragment_creatorOrigin as CreatorOrigin,
    InboxMessageFragment as MessageType,
    GetInboxThread_inboxThread as Thread,
    GetInboxThread_inboxThread_displayMessage as DisplayMessage,
    InboxSidebarThreadFragment,
    InboxThreadFragment,
} from 'nutshell-graphql-types';

import {Avatar} from '../avatar/avatar';
import {IconAvatar} from '../avatar/icon-avatar';
import {colors} from '../colors/colors';
import {
    MessageIcon,
    AlarmClockIcon,
    CircleMinusIcon,
    WarningIcon,
    ClockIcon,
    ChatIcon,
    SmsIcon,
    MessengerColoredIcon,
    InstagramColoredIcon,
    WhatsAppIcon,
    RobotIcon,
    ChatbotIcon,
    LeadIconChevron,
    NavIconEmailIcon,
    ArchiveRegularIcon,
} from '../icon';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {StatusDot} from '../status-selector/status-dot';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';
import {RelativeTimestamp} from '../timestamp/relative-timestamp';
import {Body} from '../typography/body';

import {AssignmentBanner} from './messages/banners/assignment-banner';
import {NotesBanner} from './messages/banners/notes-banner';
import {ThreadBanner} from './messages/banners/thread-banner';
import {Message} from './messages/message';
import type {ChannelStatus, Recipient, Textable, MessageSender} from './types';
import {UnidentifiedChatAvatar} from './unidentified-chat-avatar';

const getUnidentifiedRecipientName = (thread: Thread | InboxSidebarThreadFragment) => {
    const chatNumber = thread.id.split('-')[0];
    const paddedChatNumber = chatNumber?.padStart(5, '0');

    return `Unidentified-${paddedChatNumber}`;
};

const UNIDENTIFIED_RECIPIENT_ID = '-1';

export const getRecipientFromThread = (thread: Thread | InboxSidebarThreadFragment) => {
    // In the case that a phone number has no entity attached
    const UNKNOWN_RECIPIENT = {
        __typename: 'Contact',
        id: UNIDENTIFIED_RECIPIENT_ID,
        name: getUnidentifiedRecipientName(thread),
        avatarUrl: '',
        htmlUrlPath: '',
        initials: 'U',
    } as const;

    // Always prefer stubbed entity over bestEntity, in the case that someone
    // has managed to add a stubbed phone number (which is invalid anyway)
    // to a contact or account.
    const recipient = thread.stubEntity || thread.bestEntity || UNKNOWN_RECIPIENT;
    const phoneNumber = thread?.externalChannel?.phone?.numberFormattedWithCountryCode ?? '';
    const primaryEmailAddress = thread?.externalChannel?.email?.address ?? '';

    return {
        ...recipient,
        phoneNumber,
        primaryEmailAddress,
    };
};

export const getContactDisplayName = (
    thread: InboxThreadFragment,
    recipient: Pick<Recipient, 'primaryEmailAddress' | 'name'>
): string => {
    let companyName;
    let contactName;

    if (thread.bestEntity?.__typename === 'Contact') {
        companyName = thread.bestEntity?.accounts?.primary?.name;
        contactName = thread.bestEntity?.name;
    }

    return companyName || contactName || recipient.primaryEmailAddress || recipient.name;
};

export const getIsMessageSending = (message: MessageType): boolean => {
    return message.status === 'QUEUED' || message.status === 'TRANSIT';
};

export const getIsMessageScheduled = (message: MessageType): boolean => {
    return message.status === 'SCHEDULED';
};

export const getIsMessageFailed = (message: MessageType): boolean => {
    return message.status === 'FAILED';
};

export const getIsMessageCancelled = (message: MessageType): boolean => {
    return message.status === 'CANCELLED';
};

export const makeUniqueIdForTextable = (id: string, phoneNumber: string): string => {
    return `${id}${phoneNumber}`;
};

const getChannelForTextable = (textable: Textable) => {
    return textable.phone.value && textable.phone.value.channel;
};

export const getIsChannelInvalid = (status?: ChannelStatus | null): boolean => {
    return Boolean(status && status !== 'VALID');
};

export const getIsChannelBlocked = (status?: ChannelStatus | null): boolean => {
    return Boolean(status && status === 'OPTED_OUT');
};

export const isTextableDisabled = (textable: Textable): boolean => {
    const channel = getChannelForTextable(textable);

    return (
        // @ts-expect-error
        (channel && !channel.status === 'VALID') || !getFormattedPhoneNumberForTextable(textable)
    );
};

export const getFormattedPhoneNumberForTextable = (textable: Textable): string => {
    return textable.phone.value && textable.phone.value.numberFormatted
        ? textable.phone.value.numberFormatted
        : '';
};

export const getSubtitleForTextable = (textable: Textable): string | React.ReactNode => {
    const phoneNumber = getFormattedPhoneNumberForTextable(textable);

    const numberAndName = `${phoneNumber} · ${capitalize(textable.phone.name)}`;

    if (textable.phone.value.channel && textable.phone.value.channel.status === 'OPTED_OUT') {
        return (
            <div className='flex justify-sb'>
                {numberAndName}
                <CircleMinusIcon
                    color={colors.error}
                    size={15}
                    alignCenter={true}
                    data-tip='Recipient has opted out'
                    wrapWithDiv={false}
                />
            </div>
        );
    } else if (textable.phone.isFormer) {
        return (
            <div className='flex justify-sb'>
                {numberAndName}
                <ArchiveRegularIcon size={18} alignCenter={true} data-tip='Former number' />
            </div>
        );
    } else {
        return numberAndName;
    }
};

export const getThirdLineForTextable = (textable: Textable): React.ReactNode | null | undefined => {
    let text;

    const channel = getChannelForTextable(textable);
    if (!channel) {
        return null;
    } else if (
        channel.threads.latestThread &&
        channel.threads.latestThread.isOpen &&
        channel.threads.latestThread.displayMessage
    ) {
        const displayMessageTimestamp =
            channel.threads.latestThread.displayMessage.sentTime ||
            channel.threads.latestThread.displayMessage.scheduledTime ||
            channel.threads.latestThread.displayMessage.failedTime;

        text = <RelativeTimestamp datetime={displayMessageTimestamp} />;
    } else if (channel.threads.closedCount) {
        text = `${channel.threads.closedCount} closed ${
            channel.threads.closedCount === 1 ? 'thread' : 'threads'
        }`;
    } else if (getIsChannelInvalid(channel.status)) {
        return (
            <Body size='small' color='light' isItalic={true}>
                Not a valid number for SMS
            </Body>
        );
    }

    if (text) {
        return (
            <StackLegacy isHorizontal={true} spacing={6} alignCenter={true}>
                {/* @ts-expect-error */}
                <MessageIcon color={colors.blue} size={13} alignCenter={true} />
                <Body size='small' color='blue'>
                    {text}
                </Body>
            </StackLegacy>
        );
    }
};

const getThreadTypeIcon = (thread: Thread) => {
    switch (thread.threadType) {
        case 'SMS':
            return <SmsIcon size={15} alignCenter={true} />;
        case 'CHAT':
            if (thread.botStatus === ThreadBotStatus.BOT_ENABLED) {
                return <ChatbotIcon size={15} alignCenter={true} />;
            }

            return <ChatIcon size={15} alignCenter={true} />;
        case 'MESSENGER':
            // @ts-expect-error
            return <MessengerColoredIcon size={15} alignCenter={true} />;
        case 'INSTAGRAM':
            // @ts-expect-error
            return <InstagramColoredIcon size={15} alignCenter={true} />;
        case 'WHATSAPP':
            // @ts-expect-error
            return <WhatsAppIcon size={15} alignCenter={true} color={colors.whatsappGreen} />;
        case 'EMAIL':
            return <NavIconEmailIcon size={15} alignCenter={true} />;
        default:
            return null;
    }
};

const getStatusIcon = (thread: Thread) => {
    if (thread.externalChannel.status === 'OPTED_OUT' && thread.threadType === 'SMS') {
        return (
            <CircleMinusIcon
                color={colors.error}
                size={15}
                alignCenter={true}
                data-tip='Recipient has opted out'
            />
        );
    }

    if (thread.isUnread) {
        // @ts-expect-error
        return <StatusDot color={colors.red} size={8} alignCenter={true} />;
    }

    if (
        thread.effectiveStatus === 'SNOOZED' &&
        thread.snoozedUntilTime &&
        thread.threadType === 'SMS'
    ) {
        const snoozedUntilTime = thread.snoozedUntilTime;

        const snoozedTimestamp = moment.unix(snoozedUntilTime);
        const oneYearFromNow = moment().add(1, 'year');
        const isBeforeOneYearFromNow = snoozedTimestamp.isBefore(oneYearFromNow);

        return (
            <div className='flex align-center'>
                {isBeforeOneYearFromNow && (
                    <div className='mr-4'>
                        <Body size='small'>{getTinyTimestamp(snoozedUntilTime)}</Body>
                    </div>
                )}
                <ClockIcon
                    size={15}
                    alignCenter={true}
                    data-tip={
                        isBeforeOneYearFromNow
                            ? `Snoozed ${getTinyTimestamp(snoozedUntilTime)}`
                            : ''
                    }
                />
            </div>
        );
    }

    if (thread.displayMessage && thread.displayMessage.failedTime) {
        return (
            <WarningIcon
                // @ts-expect-error
                color={colors.error}
                size={15}
                alignCenter={true}
                data-tip={`Message failed ${getTinyTimestamp(thread.displayMessage.failedTime)}`}
            />
        );
    }

    if (thread.effectiveStatus === 'SCHEDULED' && thread.threadType === 'SMS') {
        return <AlarmClockIcon size={15} alignCenter={true} />;
    }
};

export const getThreadListItemHeaderIcon = (thread: Thread) => {
    return (
        <StackLegacy isHorizontal={true} spacing={8} alignCenter={true}>
            {getStatusIcon(thread)}
            {getThreadTypeIcon(thread)}
        </StackLegacy>
    );
};

export const getThreadBottomDisplayContent = (thread: Thread) => {
    if (thread.botStatus === ThreadBotStatus.BOT_ENABLED) {
        return (
            <div className='flex align-center'>
                {thread.relatedLeads && thread.relatedLeads.length > 0 ? (
                    <LeadIconChevron size={22} alignCenter={true} {...{fill: colors.greenDk}} />
                ) : (
                    <Body size='small' color='light'>
                        Not a lead yet
                    </Body>
                )}
            </div>
        );
    }

    return (
        <div className='flex align-center'>
            <Body size='small' color='light'>
                {thread.owner ? thread.owner.name : 'Unassigned'}
            </Body>
        </div>
    );
};

export const getThreadListItemTimestamp = (displayMessage: DisplayMessage) => {
    const timestamp =
        displayMessage.sentTime || displayMessage.failedTime || displayMessage.scheduledTime;

    if (timestamp) {
        const timestampMoment = moment.unix(timestamp);

        let timestampComponent;

        if (!displayMessage.sentTime && !displayMessage.failedTime) {
            timestampComponent = 'Scheduled';
        } else if (isWithinPastTimespan(timestampMoment, 1, 'minute')) {
            timestampComponent = 'Just now';
        } else if (isToday(timestampMoment) || isYesterday(timestampMoment)) {
            timestampComponent = <RelativeTimestamp datetime={timestamp} format='calendar' />;
        } else {
            timestampComponent = (
                <FormattedTimestamp
                    datetime={timestamp}
                    format={FormattedTimestamp.formats.shortMonthDayWithTime}
                />
            );
        }

        return (
            <Body size='small' color={displayMessage.failedTime ? 'error' : 'light'}>
                {timestampComponent}
            </Body>
        );
    }
};

const getThreadBannerType = (channelEvent: any) => {
    if (channelEvent.changeType === 'REOPENED') {
        return 'reopened';
    }

    const closedTimeChange = Array.isArray(channelEvent.changes)
        ? // @ts-expect-error
          channelEvent.changes.find((change) => change.attribute === 'closed_time')
        : null;

    if (!closedTimeChange) {
        return 'started';
    }

    if (!closedTimeChange.newValue) {
        return 'reopened';
    }

    return 'closed';
};

const getAssignmentBannerType = (channelEvent: any) => {
    if (channelEvent.previousAssignee) {
        return 'reassigned';
    } else if (channelEvent.changeType === 'DELETED') {
        return 'unassigned';
    } else {
        return 'assigned';
    }
};

export const renderChannelEvent = (input: {
    channelEvent: any;
    recipient: Recipient;
    onResendMessage: (messageId: string) => Promise<any>;
    onDeleteMessage: (messageId: string) => Promise<any>;
    preventResend: boolean;
    shouldShowAvatar: boolean;
    isLastEvent: boolean;
    isLastReadMessage?: boolean;
    shouldShowTimestampPopover: boolean;
    isSidebar?: boolean;
    threadType?: ThreadType;
}) => {
    const {
        channelEvent,
        recipient,
        onResendMessage,
        onDeleteMessage,
        preventResend,
        shouldShowAvatar,
        isLastEvent,
        isLastReadMessage,
        shouldShowTimestampPopover,
        isSidebar,
        threadType,
    } = input;

    switch (channelEvent.payload.__typename) {
        case 'Message': {
            const message = channelEvent.payload;
            const sender = message.isOutbound ? message.creatorUser : recipient;
            const isSending = getIsMessageSending(message);
            const isScheduled = getIsMessageScheduled(message);
            const isFailed = getIsMessageFailed(message);
            const isCancelled = getIsMessageCancelled(message);
            const dripSequenceInfo = message.edition &&
                message.edition.campaign.type === CampaignType.DRIP_SEQUENCE && {
                    position: message.edition.position,
                    total: message.edition.campaign.editions.totalCount,
                };
            const reaction = message.reaction;

            return (
                <Message
                    key={channelEvent.changeTime}
                    messageId={message.id}
                    message={message.content}
                    mediaUrl={message.mediaUrl}
                    timestampToShow={message.timestampToShow}
                    sender={sender}
                    senderEmailAddress={message.senderEmailAddress}
                    isSending={isSending}
                    scheduledTime={message.scheduledTime}
                    isScheduled={isScheduled}
                    isFailed={isFailed}
                    isCancelled={isCancelled}
                    isCancelable={message.isCancelable}
                    isOutbound={message.isOutbound}
                    onResendMessage={onResendMessage}
                    onDeleteMessage={onDeleteMessage}
                    preventResend={preventResend}
                    dripSequenceInfo={dripSequenceInfo}
                    sentTime={message.sentTime}
                    shouldShowAvatar={shouldShowAvatar}
                    isLastEvent={isLastEvent}
                    reaction={reaction}
                    errorReason={message.errorReason}
                    edition={channelEvent.payload.edition}
                    isNutshellSystemResponse={message.isNutshellSystemResponse}
                    creatorOrigin={message.creatorOrigin}
                    readTime={message.readTime}
                    isLastReadMessage={isLastReadMessage}
                    images={message.images}
                    submission={message.submission}
                    shouldShowTimestampPopover={shouldShowTimestampPopover}
                    threadType={threadType}
                    isSidebar={isSidebar}
                />
            );
        }
        case 'Thread':
            return (
                <ThreadBanner
                    key={channelEvent.changeTime}
                    actorName={
                        channelEvent.actor && channelEvent.actor.name ? channelEvent.actor.name : ''
                    }
                    changeTime={channelEvent.changeTime}
                    bannerType={getThreadBannerType(channelEvent)}
                    threadType={channelEvent.payload.threadType}
                    startedFromUrl={channelEvent.payload.startedFromUrl}
                />
            );
        case 'Assignment':
            return (
                <AssignmentBanner
                    key={channelEvent.changeTime}
                    bannerType={getAssignmentBannerType(channelEvent)}
                    changeTime={channelEvent.changeTime}
                    assigneeName={channelEvent.payload.assigneeEntity.name}
                />
            );
        case 'Note':
            return (
                <NotesBanner
                    key={channelEvent.changeTime}
                    changeTime={channelEvent.changeTime}
                    note={channelEvent.payload.bodyHtml}
                    creatorName={channelEvent.actor?.name ?? 'Someone'}
                />
            );
        default:
            return null;
    }
};

export const getRecipientFromSelectedTextable = (selectedTextable: Textable): Recipient => {
    return {
        __typename: selectedTextable.__typename,
        id: selectedTextable.id,
        name: selectedTextable.name,
        initials: selectedTextable.initials,
        avatarUrl: selectedTextable.avatarUrl,
        htmlUrlPath: selectedTextable.htmlUrlPath,
        phoneNumber: getFormattedPhoneNumberForTextable(selectedTextable),
    };
};

export const getIsBlockedFromSelectedTextable = (selectedTextable: Textable): boolean => {
    return Boolean(
        selectedTextable.phone.value.channel &&
            getIsChannelBlocked(selectedTextable.phone.value.channel.status)
    );
};

export const getFormattedTimestampForChannelEvent = (timestamp: any): React.ReactElement => {
    const changeTimeMoment = moment.unix(timestamp);

    if (isToday(changeTimeMoment)) {
        return (
            <span>
                Today,&nbsp;
                <FormattedTimestamp
                    datetime={timestamp}
                    format={FormattedTimestamp.formats.time}
                    isInline={true}
                />
            </span>
        );
    }

    if (isYesterday(changeTimeMoment)) {
        return (
            <span>
                Yesterday,&nbsp;
                <FormattedTimestamp
                    datetime={timestamp}
                    format={FormattedTimestamp.formats.time}
                    isInline={true}
                />
            </span>
        );
    }

    if (isWithinPastWeek(changeTimeMoment)) {
        return (
            <FormattedTimestamp
                datetime={timestamp}
                format={FormattedTimestamp.formats.dayWithTime}
                isInline={true}
            />
        );
    }

    if (isSameYear(changeTimeMoment)) {
        return (
            <FormattedTimestamp
                datetime={timestamp}
                format={FormattedTimestamp.formats.shortDayMonthWithAtTime}
                isInline={true}
            />
        );
    }

    return (
        <FormattedTimestamp
            datetime={timestamp}
            format={FormattedTimestamp.formats.shortDayMonthYearWithAtTime}
            isInline={true}
        />
    );
};

export const getFormattedTimestampForLastMessage = (timestamp: any): React.ReactElement => {
    const timestampMoment = moment.unix(timestamp);
    if (isToday(timestampMoment)) {
        return <FormattedTimestamp datetime={timestamp} format={FormattedTimestamp.formats.time} />;
    }

    return <FormattedTimestamp datetime={timestamp} format={FormattedTimestamp.formats.monthDay} />;
};

export const getCanReopenFromTextable = (textable?: Textable | null): boolean => {
    return Boolean(
        textable &&
            textable.phone.value.channel &&
            textable.phone.value.channel.threads.latestThread &&
            textable.phone.value.channel.threads.latestThread.canReopen
    );
};

export const getCanArchiveFromTextable = (textable?: Textable | null): boolean => {
    return Boolean(
        textable &&
            textable.phone.value.channel &&
            textable.phone.value.channel.threads.latestThread &&
            textable.phone.value.channel.threads.latestThread.canArchive
    );
};

export const getRecipientIsOnline = (thread: Thread): boolean | null | undefined => {
    if (thread.isOpen && thread.threadType === 'CHAT') {
        return thread.isActive;
    }

    return null;
};

export const getAvatar = (
    sender?: MessageSender | null,
    creatorOrigin?: CreatorOrigin | null
): React.ReactNode | null | undefined => {
    if (sender) {
        return isUnidentifiedRecipient(sender.id) ? (
            <UnidentifiedChatAvatar
                size={30}
                type={getApiTypeFromGraphQLTypename(sender.__typename)}
            />
        ) : (
            <Avatar
                avatarUrl={sender.avatarUrl}
                initials={sender.initials}
                type={getApiTypeFromGraphQLTypename(sender.__typename)}
                circular={true}
                size={30}
            />
        );
    }

    if (creatorOrigin) {
        switch (creatorOrigin.originType) {
            case 'CHAT':
            case 'CHATBOT':
                return (
                    <IconAvatar
                        customIcon={
                            <div>
                                <RobotIcon size={20} alignCenter={true} wrapWithDiv={false} />
                            </div>
                        }
                        iconSize={20}
                        iconColor={colors.white}
                        backgroundColor={colors.company}
                    />
                );
            case 'WHATSAPP':
                return (
                    <IconAvatar
                        icon={WhatsAppIcon}
                        iconSize={25}
                        iconColor={colors.whatsappGreen}
                        backgroundColor={colors.white}
                    />
                );
            default:
                return null;
        }
    }

    return null;
};

export const getRecipientDisplayName = (recipient: Recipient, thread: Thread): string => {
    switch (thread.threadType) {
        case 'WHATSAPP':
        case 'SMS':
            return recipient.name || recipient.phoneNumber;
        default:
            return recipient.name || 'Someone';
    }
};

export const isUnidentifiedRecipient = (id: string): boolean => {
    return id === UNIDENTIFIED_RECIPIENT_ID;
};
